<template>
  <div class="modal-container" :class="{ show }" v-if="form">
    <div class="modal create-edit-kid">
      <h2>Créer une année scolaire</h2>
      <font-awesome-icon :icon="['fas', 'times']" class="close" @click="hideModal" />
      <form v-if="form.step === 1" @submit.prevent="submitFirstStep">
        <div class="group">
          <label
            for="name"
            class="form"
          >Nom</label>
          <input
            type="text"
            class="form"
            id="name"
            :value="name"
            disabled
          />
        </div>
        <div class="grid">
          <div class="group">
            <label
              for="start"
              class="form"
            >Date de début</label>
            <input
              type="date"
              class="form"
              :class="{ error: form.inputsError.start }"
              id="start"
              placeholder="05/09/2021"
              v-model.trim="form.schoolYear.start"
              @change="form.inputsError.start = null"
            />
            <span class="form">{{ form.inputsError.start }}</span>
          </div>
          <div class="group">
            <label
              for="end"
              class="form"
            >Date de fin</label>
            <input
              type="date"
              class="form"
              :class="{ error: form.inputsError.end }"
              id="end"
              placeholder="05/09/2021"
              v-model.trim="form.schoolYear.end"
              @change="form.inputsError.end = null"
              :min="minimumEndDate"
              :disabled="!form.schoolYear.start"
            />
            <span class="form">{{ form.inputsError.end }}</span>
          </div>
        </div>

        <div class="grid">
          <div class="group">
            <label
              class="form"
              style="order: 0;"
            >Jours activés</label>
            <div
              class="checkbox"
              v-for="(day, idx) in form.schoolYear.enabledDays"
              :key="idx"
              :style="`order: ${idx === 0 ? 7 : idx};`"
            >
              <input
                type="checkbox"
                class="form"
                :id="`enabled-${idx}`"
                v-model="form.schoolYear.enabledDays[idx]"
                @change="form.inputsError.enabledDays = null"
              >
              <label class="form" :for="`enabled-${idx}`">{{ days[idx] }}</label>
            </div>
            <span class="form" style="order: 8;">{{ form.inputsError.enabledDays }}</span>
          </div>
          <div class="group">
            <label
              class="form"
            >Créneaux activés</label>
            <div
              class="checkbox"
              v-for="(day, idx) in form.schoolYear.slots"
              :key="idx"
            >
              <input
                type="checkbox"
                class="form"
                :id="`enabled-${idx}`"
                v-model="form.schoolYear.slots[idx].value"
              >
              <label class="form" :for="`enabled-${idx}`">{{ form.schoolYear.slots[idx].text }}</label>
            </div>
            <span class="form">{{ form.inputsError.slots }}</span>
            <div class="add-slot">
              <input
                type="text"
                class="form"
                placeholder="Matin / Midi / Soir"
                v-model.trim="form.tmpSlot"
                @keydown.enter.prevent="addSlot"
              />
              <font-awesome-icon :icon="['fas', 'plus-circle']" @click="addSlot" />
            </div>
          </div>
        </div>

        <button
          class="form full"
          type="submit"
        >Étape suivante</button>
        <button
          class="form full close"
          @click.prevent="hideModal"
        >Fermer</button>
      </form>
      <form v-if="form.step === 2" @submit.prevent="submitSecondStep">
        <table>
          <tr>
            <th>Jour</th>
            <th
              v-for="(slot, idx) in form.schoolYearSetup.general.slots"
              :key="`slot-${idx}`"
            >{{ slot.text }}</th>
          </tr>
          <tr
            v-for="(day, idx) in form.schoolYearSetup.days[form.weekIndex]"
            :key="`day-${form.weekIndex}-${idx}`"
          >
            <td class="jour">
              <span>{{ day.date.toLocaleDateString('fr-FR', dayOptions) | capitalize }}</span>
              <span v-if="day.holiday.length > 0" class="holiday">{{ day.holiday.join(' | ') }}</span>
            </td>
            <td
              v-for="(slot, i) in day.slots"
              :key="`day-${form.weekIndex}-${idx}-slot-${i}`"
              class="hoverable"
              @click="slot.enabled = !slot.enabled"
            >
              <input
                type="checkbox"
                class="form"
                v-model="slot.enabled"
              >
            </td>
          </tr>
        </table>
        <div class="grid">
          <button
            class="form full white"
            @click.prevent="prevWeek"
            :disabled="form.weekIndex === 0"
          >Semaine précédente</button>
          <button
            class="form full white"
            @click.prevent="nextWeek"
            :disabled="form.weekIndex === form.schoolYearSetup.days.length - 1"
          >Semaine suivante</button>
        </div>
        <button
          class="form full"
          type="submit"
        >Étape suivante</button>
        <button
          class="form full close"
          @click.prevent="hideModal"
        >Fermer</button>
      </form>
      <form v-if="form.step === 3" @submit.prevent="submitThirdStep">
        <h3>Redoublement</h3>

        <table>
          <tr>
            <th>Prénom</th>
            <th>Nom</th>
            <th>Classe</th>
            <th>Selectionner</th>
          </tr>
          <tr
            v-for="(kid, idx) in kids"
            :key="`kid-${idx}`"
          >
            <td>{{ kid.firstname }}</td>
            <td>{{ kid.lastname }}</td>
            <td>{{ grades[Number(kid.grade)] }}</td>
            <td
              class="hoverable"
              @click="$set(repeating, idx, !repeating[idx])"
            >
              <input
                type="checkbox"
                class="form"
                v-model="repeating[idx]"
              >
            </td>
          </tr>
        </table>

        <button
          class="form full"
          type="submit"
        >Créer l'année scolaire</button>
        <button
          class="form full close"
          @click.prevent="hideModal"
        >Fermer</button>
      </form>
    </div>
  </div>
</template>

<script>
import { add, sub, format, isBefore, isAfter, eachDayOfInterval, isWithinInterval } from 'date-fns'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { getPublicHolidays, getSchoolHolidays } from '@/services/holidays'

const defaultFormData = {
  schoolYear: {
    start: null,
    end: null,
    enabledDays: [
      false,
      false,
      false,
      false,
      false,
      false,
      false,
    ],
    slots: [],
  },
  tmpSlot: null,
  inputsError: {
    start: null,
    end: null,
    slots: null,
    enabledDays: null,
  },
  step: 1,
  weekIndex: 0,
  schoolYearSetup: {
    general: null,
    days: null,
  },
}

export default {
  name: 'CreateSchoolYear',
  components: {
  },
  data() {
    return {
      show: false,
      form: null,
      days: [
        'Dimanche',
        'Lundi',
        'Mardi',
        'Mercredi',
        'Jeudi',
        'Vendredi',
        'Samedi',
      ],
      dayOptions: {
        weekday: 'long',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      },
      grades: [
        'Petite Section',
        'Moyenne Section',
        'Grande Section',
        'CP',
        'CE1',
        'CE2',
        'CM1',
        'CM2',
      ],
      kids: [],
      repeating: [],
      publicHolidays: null,
      schoolHolidays: null
    }
  },
  computed: {
    ...mapGetters('kids', ['getKids']),
    name() {
      return `Année scolaire ${this.form.schoolYear.start?.split('-')[0] || ''}/${this.form.schoolYear.end?.split('-')[0] || ''}`
    },
    minimumEndDate() {
      return format(add(new Date(this.form.schoolYear.start), { days: 1 }), 'yyyy-MM-dd')
    }
  },
  methods: {
    ...mapActions('schoolyears', ['createSchoolYear']),
    ...mapActions('kids', ['fetchAllKids', 'updateKid', 'deleteKid']),
    ...mapMutations('loading', ['setLoading']),
    showModal() {
      this.form = this.lodash.cloneDeep(defaultFormData)
      this.fetchAllKids()
      getPublicHolidays().then(res => {
        this.publicHolidays = res
      })
      this.show = true
    },
    hideModal(e, skip = false) {
      if (skip || confirm('Voulez vous vraiment quitter ? Rien ne sera sauvegardé.')) {
        this.show = false
        this.form = null
      }
    },
    addSlot() {
      if (this.form.tmpSlot?.length > 0) {
        this.form.schoolYear.slots.push({
          text: this.form.tmpSlot,
          slug: this.form.tmpSlot.replace(/\s+/g, '-').toLowerCase(),
          value: true
        })
        this.form.tmpSlot = null
        this.form.inputsError.slots = null
      }
    },
    prevWeek() {
      if (this.form.weekIndex === 0) return
      this.form.weekIndex -= 1
    },
    nextWeek() {
      if (this.form.weekIndex === this.form.schoolYearSetup.days.length - 1) return
      this.form.weekIndex += 1
    },
    dateInArray(date, array) {
      if (!Array.isArray(array)) return false
      if (array.some(d => +d.date === +date)) {
        return array.find(d => +d.date === +date)
      }
      return false
    },
    async submitFirstStep() {
      let error = false

      Object.keys(this.form.schoolYear).forEach(key => {
        if (key === "name") return
        if (this.form.schoolYear[key] === null) {
          this.form.inputsError[key] = 'Ce champ est obligatoire'
          error = true
        } else if (key === 'enabledDays') {
          let oneDayEnabled = false
          this.form.schoolYear[key].forEach(day => {
            if (day) oneDayEnabled = true
          })
          if (!oneDayEnabled) {
            error = true
            this.form.inputsError[key] = 'Ce champ est obligatoire'
          }
        } else if (this.form.schoolYear[key].length < 1) {
          this.form.inputsError[key] = 'Ce champ est obligatoire'
          error = true
        }
      })

      if (!error &&
        !isAfter(new Date(this.form.schoolYear.end), new Date(this.form.schoolYear.start))) {
        error = true
        this.form.inputsError.end = 'La date de fin doit être après la date de début'
      }

      if (error) return

      let data = JSON.parse(JSON.stringify(this.form.schoolYear))

      data.name = this.name

      data.start = new Date(`${data.start} 00:00:00`)
      data.end = new Date(`${data.end} 00:00:00`)
      data.active = false
      
      try {
        this.setLoading(true)
        let response = await getSchoolHolidays(`${data.start.getFullYear()}-${data.end.getFullYear()}`)
        this.schoolHolidays = response.results
        this.setLoading(false)
      } catch {
        this.setLoading(false)
        alert('Impossible de récupérer le calendrier scolaire auprès du gouvernement.')
      }

      data.slots = data.slots.filter(slot => {
        if (slot.value) {
          return true
        }
        return false
      }).map(slot => ({
        text: slot.text,
        slug: slot.slug
      }))

      const datesInterval = eachDayOfInterval({
        start: data.start, 
        end: data.end
      })

      let tmpDates = []
      let lastDay = 0
      let isSunday = false
      let dates = []

      data.enabledDays.forEach((day, idx) => {
        if (day && idx === 0) {
          isSunday = true
        }
        if (day && idx > lastDay) {
          lastDay = idx
        }
      })

      let publicHolidays = null
      if (this.publicHolidays) {
        publicHolidays = []
        Object.keys(this.publicHolidays).forEach(key => {
          let date = new Date(`${key} 00:00:00`)
          if (!isBefore(date, data.start) && !isAfter(date, data.end)) {
            publicHolidays.push({
              date,
              text: this.publicHolidays[key]
            })
          }
        })
      }

      datesInterval.forEach((date, idx) => {
        const day = date.getDay();
        
        if (data.enabledDays[day]) {
          let holiday = []
          
          this.schoolHolidays.forEach(el => {
            if (el.start_date === el.end_date) return
            const start = add(new Date(`${el.start_date.substring(0, 10)} 00:00:00`), {days: 1})
            const end = sub(new Date(`${el.end_date.substring(0, 10)} 00:00:00`), {days: 1})

            if (isWithinInterval(date, {
              start,
              end
            })) {
              holiday.push(el.description)
            }
          })

          const publicHoliday = this.dateInArray(date, publicHolidays)
          if (publicHoliday) {
            holiday.push(publicHoliday.text)
          }
          tmpDates.push({
            date,
            holiday,
            slots: data.slots.map(slot => ({
              name: slot.text,
              slug: slot.slug,
              enabled: holiday.length > 0 ? false : true,
              kids: [],
            })),
          })
        }

        if (!isSunday && day === lastDay) {
          dates.push(tmpDates)
          tmpDates = []
        } else if (isSunday && day === 0) {
          dates.push(tmpDates)
          tmpDates = []
        } else if (idx === datesInterval.length - 1) {
          dates.push(tmpDates)
          tmpDates = []
        }
      })

      this.form.schoolYearSetup.general = data
      this.form.schoolYearSetup.days = dates
      this.form.step = 2
    },
    submitSecondStep() {
      this.kids = this.lodash.cloneDeep(this.getKids)
      this.kids = this.kids.filter(kid => kid.verified)
      if (this.kids.length < 1) {
        this.submitThirdStep(null, true)
        return
      }
      this.kids.sort((a, b) => {
        if (a.grade < b.grade) {
          return -1
        } else if (a.grade > b.grade) {
          return 1
        }
        return a.firstname.localeCompare(b.firstname, 'fr', { sensitivity: 'base' })
      })
      this.kids.forEach(() => this.repeating.push(false))
      this.form.step = 3
    },
    submitThirdStep(e, skip = false) {
      const documentId = `${this.form.schoolYearSetup.general.start.getFullYear()}-${this.form.schoolYearSetup.general.end.getFullYear()}`
      this.createSchoolYear({
        docId: documentId,
        schoolYear: this.form.schoolYearSetup.general,
        schoolYearDays: this.form.schoolYearSetup.days
      })
      if (!skip) {
        this.repeating.forEach((val, idx) => {
          let kid = this.kids[idx]
          if (!val) {
            if (kid.grade === 7) {
              this.deleteKid({
                uid: kid.uid,
                dispatchName: null
              })
            } else {
              this.updateKid({
                data: {
                  uid: kid.uid,
                  kid: {
                    grade: kid.grade + 1,
                    verified: false
                  }
                },
                dispatchName: null
              })
            }
          } else {
            this.updateKid({
              data: {
                uid: kid.uid,
                kid: {
                  verified: false
                }
              },
              dispatchName: null
            })
          }
        })
        this.fetchAllKids()
      }


      this.hideModal(null, true)
    }
  },
}
</script>

<style lang="scss" scoped>
svg.close {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 20px;
  cursor: pointer;
  display: none;

  @media screen and (min-width: 768px) {
    display: block;
  }
}

form {
  width: 100%;
  display: flex;
  flex-direction: column;

  div.grid {
    width: 100%;
    display: flex;
    flex-direction: column;

    @media screen and (min-width: 768px) {
      min-width: 420px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 10px 20px;
    }
  }

  div.group {
    display: flex;
    flex-direction: column;
  }

  div.checkbox {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    label {
      margin: 0;
    }
  }

  div.add-slot {
    display: flex;
    justify-content: space-between;
    align-items: center;

    input {
      width: calc(100% - 30px) !important;
      margin-bottom: 0 !important;
    }

    svg {
      font-size: 25px;
      color: #3490dc;
      transition: color .2s ease;
      cursor: pointer;

      &:hover {
        color: #2779bd;
      }
    }
  }

  button {
    margin: 10px 0 0 !important;

    &.close {
      margin: 10px 0 0 !important;
      background-color: white;
      color: #3490dc;
      border: 1px solid #3490dc;

      @media screen and (min-width: 768px) {
        display: none;
      }
    }

    &.white {
      border: 1px solid #3490dc;
      background-color: white;
      color: #22292f;

      &:hover {
        background-color: #5fa5df;
      }
    }
  }

  table {
    display: block;
    font-family: sans-serif;
    -webkit-font-smoothing: antialiased;
    font-size: 115%;
    overflow: auto;
    width: auto;
    
    th {
      background-color: #509edd;
      color: white;
      font-weight: normal;
      padding: 20px 30px;
      text-align: center;
    }
    td {
      background-color: rgb(238, 238, 238);
      color: rgb(111, 111, 111);
      padding: 0 30px;
      text-align: center;
      height: 60px;

      &.hoverable {
        cursor: pointer;
        
        &:hover {
          background-color: rgb(211, 211, 211);
        }
      }

      &.jour {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        span.holiday {
          font-size: 10px;
        }
      }
    }
  }
}
</style>